/**
 * The movement parameters exposed for live tuning, with safe ranges and
 * plain-language explanations, plus derived quantities that translate raw
 * coefficients into things a designer can feel ("reaches run speed in 0.21 s").
 * Ranges are deliberately narrower than validateConfig() so every slider
 * position is a valid, stable configuration.
 */
import type { MovementConfig } from '../core/Config';
import type { MovementRuleSet } from '../movement/MovementRules';

export type TunableGroup = 'Acceleration' | 'Directional control' | 'Gravity & jump' | 'Surface contact' | 'Momentum' | 'Momentum model' | 'Bending (A)' | 'Compression (B)' | 'Coupling (C)';

export type TunableTarget = { kind: 'movement'; field: keyof MovementConfig } | { kind: 'rule'; rule: string; param: string };

export interface Tunable {
  id: string;
  group: TunableGroup;
  label: string;
  help: string;
  min: number;
  max: number;
  step: number;
  target: TunableTarget;
}

const m = (field: keyof MovementConfig, group: TunableGroup, label: string, help: string, min: number, max: number, step: number): Tunable => ({
  id: `m.${field}`,
  group,
  label,
  help,
  min,
  max,
  step,
  target: { kind: 'movement', field },
});

const r = (rule: string, param: string, group: TunableGroup, label: string, help: string, min: number, max: number, step: number): Tunable => ({
  id: `r.${rule}.${param}`,
  group,
  label,
  help,
  min,
  max,
  step,
  target: { kind: 'rule', rule, param },
});

export const TUNABLES: readonly Tunable[] = [
  m('runSpeed', 'Acceleration', 'Run speed (m/s)', 'Top speed reachable by input alone; above it you are in the momentum regime.', 5, 14, 0.1),
  m('groundAccelRate', 'Acceleration', 'Ground acceleration (1/s)', 'How quickly velocity approaches the input target while a key is held.', 3, 30, 0.5),
  m('groundDecelRate', 'Acceleration', 'Ground deceleration (1/s)', 'How quickly you stop with no input.', 3, 30, 0.5),
  m('groundTurnRate', 'Directional control', 'Ground turn rate (1/s)', 'How fast steering rotates velocity above run speed (before speed falloff).', 2, 30, 0.5),
  m('turnSpeedRef', 'Directional control', 'Turn falloff speed (m/s)', 'Speed at which turning is half as effective. Higher = easier high-speed cornering.', 4, 40, 0.5),
  m('airControlSpeed', 'Directional control', 'Air control speed (m/s)', 'Below this planar speed, air input can still build velocity.', 0, 10, 0.1),
  m('airAccelRate', 'Directional control', 'Air acceleration (1/s)', 'Air control strength below air control speed.', 0, 8, 0.1),
  m('airTurnRate', 'Directional control', 'Air turn rate (1/s)', 'How fast air steering rotates velocity above air control speed.', 0, 8, 0.1),
  m('gravity', 'Gravity & jump', 'Gravity (m/s²)', 'Downward acceleration. Changes jump height, airtime and crest behaviour.', 10, 40, 0.5),
  m('jumpSpeed', 'Gravity & jump', 'Jump speed (m/s)', 'Upward impulse of a jump. Apex height = v²/2g.', 4, 12, 0.1),
  m('jumpNormalBlend', 'Gravity & jump', 'Jump slope tilt', 'How much a jump leans along the ground normal on slopes (0 = straight up).', 0, 0.8, 0.05),
  m('coyoteTicks', 'Gravity & jump', 'Coyote time (ticks)', 'Grace period after leaving ground in which a jump still works (120 ticks = 1 s).', 0, 30, 1),
  m('jumpBufferTicks', 'Gravity & jump', 'Jump buffer (ticks)', 'A jump pressed this early before landing still fires on landing.', 0, 30, 1),
  m('groundSeparationTolerance', 'Surface contact', 'Crest stickiness (m/s per tick)', 'How much "lift-off" velocity still counts as staying on the ground. Higher = glued to crests longer.', 0.05, 2, 0.05),
  m('snapMaxSeparationSpeed', 'Surface contact', 'Down-snap limit (m/s)', 'Largest separation the ground snap absorbs when walking off ledges and down slopes.', 0.5, 5, 0.1),
  m('maxGroundAngleDeg', 'Surface contact', 'Max walkable slope (°)', 'Steeper surfaces are walls.', 30, 70, 1),
  m('momentumDecayRate', 'Momentum', 'Momentum decay (1/s)', 'How fast speed above run speed bleeds away on the ground. Half-life = ln2 / rate.', 0, 2, 0.01),
  m('brakeDecel', 'Momentum', 'Brake strength (m/s²)', 'Deceleration when steering against your motion above run speed.', 4, 60, 1),
  m('contactContinuityDeg', 'Momentum model', 'Seam continuity (°)', 'Sliding across a seam this shallow keeps speed (faceted curves act as curves). 0 = Phase 2: every seam loses 1 − cos Δθ.', 0, 30, 1),
  m('airSteerKeepsSpeed', 'Momentum model', 'Air steer keeps speed', '1 = below air control speed, steering never removes speed unless it opposes your motion. 0 = Phase 2.', 0, 1, 1),
  m('airLeadGain', 'Momentum model', 'Lead steering gain', 'Air turn-rate multiplier while your input leads the velocity by at most the lead angle. 1 = off.', 0.5, 3, 0.05),
  m('airLeadSideGain', 'Momentum model', 'Side steering gain', 'Air turn-rate multiplier for input 90° or more from the velocity (pure strafe). 1 = off.', 0.2, 2, 0.05),
  m('airLeadAngleDeg', 'Momentum model', 'Lead angle (°)', 'Largest lead that still gets the full lead gain.', 5, 60, 1),
  m('slopeGain', 'Momentum', 'Slope gain', 'Fraction of gravity converted along slopes at speed (downhill acceleration).', 0, 2, 0.05),
  r('exp.bending', 'maxTurnRate', 'Bending (A)', 'Bend turn rate (rad/s)', 'Strongest curve-following turn at contact on a grazing pass.', 0, 12, 0.1),
  r('exp.bending', 'range', 'Bending (A)', 'Bend range (m)', 'Distance within which bend surfaces act.', 0.2, 4, 0.1),
  r('exp.bending', 'inputGate', 'Bending (A)', 'Requires steering in', '1 = only bends while you steer toward the surface; 0 = always.', 0, 1, 1),
  r('exp.compression.store', 'storeFraction', 'Compression (B)', 'Stored fraction', 'Share of impact speed stored as charge.', 0, 1, 0.05),
  r('exp.compression.release', 'efficiency', 'Compression (B)', 'Release efficiency', 'Share of the stored charge returned by the release jump.', 0, 1, 0.05),
  r('exp.compression.release', 'windowTicks', 'Compression (B)', 'Release window (ticks)', 'How long after impact a jump releases the charge.', 4, 120, 1),
  r('exp.compression.release', 'decayRate', 'Compression (B)', 'Charge decay (1/s)', 'How quickly the charge fades inside the window.', 0, 20, 0.5),
  r('exp.coupling', 'strength', 'Coupling (C)', 'Coupling strength (m/s²)', 'Peak pull toward coupling surfaces at speed.', 0, 200, 5),
  r('exp.coupling', 'range', 'Coupling (C)', 'Coupling range (m)', 'Distance over which coupling acts.', 0.2, 4, 0.1),
  r('exp.coupling', 'speedRef', 'Coupling (C)', 'Coupling half-speed (m/s)', 'Tangential speed at which coupling reaches half strength.', 1, 40, 0.5),
];

export function readTunable(t: Tunable, movement: Readonly<MovementConfig>, rules: MovementRuleSet): number {
  return t.target.kind === 'movement' ? movement[t.target.field] : rules.get(t.target.rule).params[t.target.param]!;
}

export interface DerivedReadout {
  label: string;
  value: string;
  help: string;
}

/** Plain-language consequences of the current parameters, computed analytically. */
export function deriveReadouts(mv: Readonly<MovementConfig>, tickRate: number): DerivedReadout[] {
  const g = mv.gravity;
  const turnAt = (s: number): number => mv.groundTurnRate / (1 + s / mv.turnSpeedRef);
  const fast = mv.runSpeed * 2;
  const crestRadius = 4;
  const crestHold = Math.sqrt((g + mv.groundSeparationTolerance * tickRate) * crestRadius);
  const halfLife = mv.momentumDecayRate > 0 ? Math.LN2 / mv.momentumDecayRate : Infinity;
  return [
    { label: 'Reach 90% run speed', value: `${(Math.LN10 / mv.groundAccelRate).toFixed(2)} s`, help: 'Responsiveness from standstill.' },
    { label: 'Stop from run speed', value: `${(Math.log(mv.runSpeed / 0.1) / mv.groundDecelRate).toFixed(2)} s`, help: 'Time to drop below 0.1 m/s with no input.' },
    { label: 'Jump apex', value: `${((mv.jumpSpeed * mv.jumpSpeed) / (2 * g)).toFixed(2)} m`, help: 'Flat-ground jump height, v²/2g.' },
    { label: 'Jump airtime', value: `${((2 * mv.jumpSpeed) / g).toFixed(2)} s`, help: 'Flat-ground jump duration, 2v/g.' },
    { label: 'Running jump length', value: `${((2 * mv.jumpSpeed * mv.runSpeed) / g).toFixed(1)} m`, help: 'Horizontal distance of a jump at run speed.' },
    { label: 'Momentum half-life', value: Number.isFinite(halfLife) ? `${halfLife.toFixed(1)} s` : 'never', help: 'Time for speed above run speed to halve on the ground.' },
    { label: `Turn radius at ${fast.toFixed(0)} m/s`, value: `${(fast / turnAt(fast)).toFixed(1)} m`, help: 'Tightest steady ground curve at twice run speed.' },
    { label: 'Holds a 4 m crest up to', value: `${crestHold.toFixed(1)} m/s`, help: 'Above this you launch off a 4 m-radius hump (approximate).' },
  ];
}
